import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { getClassPoints, getClassPointHistories, ClassPointHistoryItem, isTeacher } from '../../../../utils/firebase';
import { useIsMobile } from '../../../../utils/useMediaQuery';
import Button from '../../../../components/ui/Button';
import AddClassPointsDialog from '../../../../components/ui/AddClassPointsDialog';
import Card from '../../../../components/ui/Card';

const pxToRem = (value: number) => `${value / 16}rem`;

/** 온도계 형태 학급 포인트 시각화 */
const ClassPointGauge: React.FC<{
  currentPoints: number;
  maxPoints: number;
  height?: number;
}> = ({ currentPoints, maxPoints, height = 400 }) => {
  const fillPercent = maxPoints <= 0 ? 0 : Math.min(100, (currentPoints / maxPoints) * 100);
  const safeMax = Math.max(1, maxPoints);
  const barWidth = 56;
  const barRadius = barWidth / 2;
  const fillRadius = barRadius - 2;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: pxToRem(14),
        width: '100%',
      }}
    >
      <span
        style={{
          color: 'var(--Color-text-assistive, #989898)',
          fontSize: pxToRem(15),
          fontWeight: 500,
          fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
        }}
      >
        학급 포인트
      </span>
      {/* 눈금(왼쪽) + 온도계 바 */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          gap: pxToRem(12),
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            height: pxToRem(height),
            minWidth: pxToRem(40),
            fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
            fontSize: pxToRem(13),
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--Color-text-assistive, #989898)',
          }}
        >
          <span>{safeMax.toLocaleString()}</span>
          <span>{Math.floor(safeMax / 2).toLocaleString()}</span>
          <span>0</span>
        </div>
        {/* 온도계 바 외곽 */}
        <div
          style={{
            width: pxToRem(barWidth),
            height: pxToRem(height),
            borderRadius: pxToRem(barRadius),
            backgroundColor: 'var(--Color-background-sub, #F8F8F8)',
            border: '2px solid var(--Color-line-normal, #F0F0F0)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          {/* 채워진 영역 (아래에서 위로) */}
          <div
            style={{
              width: '100%',
              height: `${fillPercent}%`,
              minHeight: currentPoints > 0 ? pxToRem(4) : 0,
              borderRadius: pxToRem(fillRadius),
              backgroundColor: 'var(--Color-fill-brand-color, #FF6F0F)',
              transition: 'height 0.4s ease',
            }}
          />
        </div>
      </div>
      <span
        style={{
          color: 'var(--Color-text-strong, #0D0D0D)',
          fontSize: pxToRem(28),
          fontWeight: 600,
          fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
        }}
      >
        {currentPoints.toLocaleString()}pt
      </span>
    </div>
  );
};

const ClassPointManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const { currentUser } = useAuth();
  const [classPoints, setClassPoints] = useState<number>(0);
  const [histories, setHistories] = useState<ClassPointHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const schoolId = currentUser && isTeacher(currentUser) ? currentUser.schoolId : '';
  const classId = currentUser && isTeacher(currentUser) ? currentUser.classId : '';

  const fetchData = useCallback(async () => {
    if (!schoolId || !classId) return;
    setLoading(true);
    setError(null);
    try {
      const [points, list] = await Promise.all([
        getClassPoints(schoolId, classId),
        getClassPointHistories(schoolId, classId),
      ]);
      setClassPoints(points);
      setHistories(list);
    } catch (err) {
      console.error('학급 포인트 조회 오류:', err);
      setError('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [schoolId, classId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** 게이지 최대값 (시각화용, 여유 있게) */
  const gaugeMax = useMemo(() => Math.max(100, Math.ceil((classPoints + 80) / 50) * 50), [classPoints]);

  if (!currentUser || !isTeacher(currentUser) || !schoolId || !classId) {
    return (
      <div style={{ padding: 24, color: 'var(--Color-text-normal, #161616)', fontSize: 14 }}>
        담임 학급이 없거나 권한이 없습니다.
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        minHeight: 'fit-content',
      }}
    >
      {/* 헤더 영역 - StoreManagement·ClassTeacherHome 등과 동일 */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
          marginBottom: '32px',
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            color: 'var(--Color-text-normal, #161616)',
            fontSize: '32px',
            fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
            fontWeight: 600,
            lineHeight: '48px',
            wordWrap: 'break-word',
          }}
        >
          학급 포인트 관리
        </div>
        <Button
          width="118px"
          height="40px"
          variant="submit"
          onClick={() => setIsAddDialogOpen(true)}
          disabled={loading}
        >
          지급 / 차감
        </Button>
      </div>

      {loading ? (
        <div
          style={{
            padding: pxToRem(32),
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: pxToRem(14),
          }}
        >
          로딩 중...
        </div>
      ) : error ? (
        <div
          style={{
            padding: pxToRem(16),
            borderRadius: pxToRem(8),
            backgroundColor: 'rgba(255, 56, 56, 0.1)',
            color: 'var(--Color-status-negative, #FF3838)',
            fontSize: pxToRem(14),
          }}
        >
          {error}
        </div>
      ) : (
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '24px',
            alignItems: 'stretch',
          }}
        >
          {/* 왼쪽: 온도계 시각화 카드 */}
          <Card
            width={isMobile ? '100%' : '400px'}
            padding={pxToRem(28)}
            style={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: isMobile ? undefined : '520px',
            }}
          >
            <ClassPointGauge
              currentPoints={classPoints}
              maxPoints={gaugeMax}
              height={400}
            />
          </Card>

          {/* 오른쪽: 현재 포인트 요약 + 이력 테이블 */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            {/* 현재 학급 포인트 요약 카드 */}
            <Card
              width="100%"
              padding="24px 28px"
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: pxToRem(16),
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: pxToRem(6) }}>
                <span
                  style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: pxToRem(15),
                    fontWeight: 500,
                    fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                  }}
                >
                  현재 학급 포인트
                </span>
                <span
                  style={{
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    fontSize: pxToRem(36),
                    fontWeight: 600,
                    fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                  }}
                >
                  {classPoints.toLocaleString()}pt
                </span>
              </div>
            </Card>

            {/* 지급/차감 이력 */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: pxToRem(12),
              }}
            >
              <span
                style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: pxToRem(18),
                  fontWeight: 600,
                  fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                }}
              >
                지급/차감 이력
              </span>
              <Card
                width="100%"
                padding="0"
                style={{
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '16px',
                }}
              >
                {histories.length === 0 ? (
                  <div
                    style={{
                      padding: pxToRem(32),
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: pxToRem(14),
                      textAlign: 'center',
                      fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                    }}
                  >
                    이력이 없습니다.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                        fontSize: pxToRem(14),
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: 'var(--Color-background-sub, #F8F8F8)',
                            borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                          }}
                        >
                          <th
                            style={{
                              padding: pxToRem(12),
                              textAlign: 'left',
                              fontWeight: 600,
                              color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                            }}
                          >
                            일시
                          </th>
                          <th
                            style={{
                              padding: pxToRem(12),
                              textAlign: 'right',
                              fontWeight: 600,
                              color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                            }}
                          >
                            포인트
                          </th>
                          <th
                            style={{
                              padding: pxToRem(12),
                              textAlign: 'left',
                              fontWeight: 600,
                              color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                            }}
                          >
                            사유
                          </th>
                          <th
                            style={{
                              padding: pxToRem(12),
                              textAlign: 'left',
                              fontWeight: 600,
                              color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                            }}
                          >
                            처리자
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {histories.map((item) => (
                          <tr
                            key={item.id}
                            style={{
                              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                            }}
                          >
                            <td
                              style={{
                                padding: pxToRem(12),
                                color: 'var(--Color-text-normal, #161616)',
                              }}
                            >
                              {item.createdAt.toLocaleString('ko-KR')}
                            </td>
                            <td
                              style={{
                                padding: pxToRem(12),
                                textAlign: 'right',
                                color:
                                  item.amount >= 0
                                    ? 'var(--Color-status-positive, #00C853)'
                                    : 'var(--Color-status-negative, #FF3838)',
                                fontWeight: 500,
                              }}
                            >
                              {item.amount >= 0 ? '+' : ''}
                              {item.amount.toLocaleString()}pt
                            </td>
                            <td
                              style={{
                                padding: pxToRem(12),
                                color: 'var(--Color-text-normal, #161616)',
                              }}
                            >
                              {item.reason || '-'}
                            </td>
                            <td
                              style={{
                                padding: pxToRem(12),
                                color: 'var(--Color-text-assistive, #989898)',
                              }}
                            >
                              {item.teacherName || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      )}

      <AddClassPointsDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        schoolId={schoolId}
        classId={classId}
        currentClassPoints={classPoints}
        teacherId={currentUser.uid}
        teacherName={currentUser.name || '선생님'}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default ClassPointManagement;
